import { supabase } from '../lib/supabase'

/**
 * Fetch all groups visible to the current user (owned + member of).
 * Returns groups with nested member player data and an isOwner flag.
 */
export async function fetchMyGroups() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Auth session not available')

  const { data, error } = await supabase
    .from('player_groups')
    .select('*, members:player_group_members(*, player:players(*))')
    .order('name')

  if (error) throw error

  return (data || []).map((g) => ({
    ...g,
    isOwner: g.owner_id === user.id,
  }))
}

/**
 * Fetch a single group with all members.
 */
export async function fetchGroupDetail(groupId) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Auth session not available')

  const { data, error } = await supabase
    .from('player_groups')
    .select('*, members:player_group_members(*, player:players(*))')
    .eq('id', groupId)
    .single()

  if (error) throw error

  return {
    ...data,
    isOwner: data.owner_id === user.id,
  }
}

/**
 * Create a new group.
 */
export async function createGroup({ name }) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('player_groups')
    .insert({ name, owner_id: user.id })
    .select()
    .single()

  if (error) throw error

  // Auto-add the creator as a group member
  const { data: myPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle()

  if (myPlayer) {
    await supabase
      .from('player_group_members')
      .insert({ group_id: data.id, player_id: myPlayer.id })
  }

  return data
}

/**
 * Update a group's name.
 */
export async function updateGroup(groupId, { name }) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('player_groups')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', groupId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a group (CASCADE removes members).
 */
export async function deleteGroup(groupId) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { error } = await supabase
    .from('player_groups')
    .delete()
    .eq('id', groupId)

  if (error) throw error
}

/**
 * Replace all members of a group with a new set of player IDs.
 * Delete-all then insert-all (RLS ensures only owner can do this).
 */
export async function bulkSetGroupMembers(groupId, playerIds) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  // Remove all existing members
  const { error: deleteError } = await supabase
    .from('player_group_members')
    .delete()
    .eq('group_id', groupId)

  if (deleteError) throw deleteError

  // Insert new members
  if (playerIds.length > 0) {
    const rows = playerIds.map((playerId) => ({ group_id: groupId, player_id: playerId }))
    const { error: insertError } = await supabase
      .from('player_group_members')
      .insert(rows)

    if (insertError) throw insertError
  }
}
